<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  BaseIconButton,
  BaseSelectBox
} from '@cplug/design-system/components'
import {
  BaseButtonSize,
  BaseButtonType,
  BaseIconName,
  BaseSelectBoxSize,
  BaseSelectBoxVariant,
  MediaQuery
} from '@cplug/design-system/components/props'
import { useMediaQuery } from '@vueuse/core'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { useMyPlanStore } from '@cplug/frontend-subscription-hub/domain/plans/my-plan'
import { useWhitelabelStore } from '@cplug/frontend-shared-domains/domain/whitelabel'
import { useHeaderCompanySelectSearch } from '../composables/useHeaderCompanySelectSearch'
import { useTranslation } from './BaseHeaderRightSide.i18n'
import useMenuSidebarStore from '~/shared/stores/useMenuSidebarStore'
import type { Company } from '~/domain/shared/company/models/Company'
import useLoggedUserStore from '~/domain/dashboard/user/logged-user/stores/useLoggedUserStore'
import { useResearchCustomerPulse } from '~/domain/shared/research-customer/composables/useResearchCustomerPulse'

const { t } = useTranslation()

const authStore = useAuthenticationStore()
const { companies: selectedCompanies } = storeToRefs(authStore)

const menuSidebarStore = useMenuSidebarStore()
const { assistantHidesMenu, isSecondaryPanelExpanded } = storeToRefs(menuSidebarStore)
const myPlanStore = useMyPlanStore()
const whitelabelStore = useWhitelabelStore()
const loggedUserStore = useLoggedUserStore()
const isDesktop = useMediaQuery(MediaQuery.DesktopWithSidebar)
const isCompactMobile = useMediaQuery('(max-width: 414px)')

const shouldShowMenuButton = computed(() =>
  !isDesktop.value || assistantHidesMenu.value
)

const {
  selectOptions,
  isRemoteSearchLoading,
  companies,
  pageMeta,
  onFilter,
  onDropdownHide,
  findCompanyById
} = useHeaderCompanySelectSearch()

const companySelectClass = computed(() => {
  if (isDesktop.value) {
    return 'min-w-0 max-w-xs shrink'
  }

  if (isCompactMobile.value) {
    return 'min-w-0 max-w-[110px] shrink'
  }

  return 'min-w-0 max-w-[200px] shrink'
})

const companySelectSize = computed(() => {
  return isDesktop.value ? BaseSelectBoxSize.Medium : BaseSelectBoxSize.Small
})

myPlanStore.getMyPlan()
whitelabelStore.getSettings()
useResearchCustomerPulse()

const selectedCompany = ref<Company | undefined>(selectedCompanies.value[0])
const hasMultipleCompanies = ref(false)

function updateHasMultipleCompanies (): void {
  const isMultiple = (pageMeta.value?.total ?? 0) > 1 || (companies.value?.length ?? 0) > 1
  if (isMultiple) {
    hasMultipleCompanies.value = true
  }
}

watch([companies, pageMeta], updateHasMultipleCompanies, { immediate: true })

const selectedCompanyValue = computed({
  get: () => selectedCompany.value?.id,
  set: (newCompanyId: number | undefined) => {
    if (newCompanyId) {
      const company = findCompanyById(newCompanyId)
      if (company) {
        selectedCompany.value = company
        authStore.setCompanies([company])
        myPlanStore.model = undefined
        loggedUserStore.clearPermissions()

        const currentRoute = window.location.pathname
        const blockedRoutes = ['/dashboard/blocked-plan', '/dashboard/overdue-payment']

        if (blockedRoutes.includes(currentRoute)) {
          window.location.href = '/dashboard'
          return
        }
        window.location.reload()
      }
    } else {
      selectedCompany.value = undefined
      authStore.setCompanies([])
    }
  }
})

watch(() => companies.value, (loadedCompanies) => {
  const currentAuthCompany = selectedCompanies.value[0]
  if (!currentAuthCompany?.id || currentAuthCompany.contract) {
    return
  }

  const fullCompany = loadedCompanies?.find(company => company.id === currentAuthCompany.id)
  if (fullCompany) {
    authStore.setCompanies([fullCompany])
    selectedCompany.value = fullCompany
  }
})

function onClickMenuButton (): void {
  menuSidebarStore.toogle()
}

function onClickSidebarToggle (): void {
  menuSidebarStore.toggleSecondaryPanel()
}

function onSearchCompanies (value: string): void {
  onFilter(value)
}

const sidebarToggleIcon = computed(() =>
  isSecondaryPanelExpanded.value ? BaseIconName.MenuContract : BaseIconName.MenuExpand
)
</script>

<template>
  <div class="flex min-w-0 flex-1 items-center gap-2 max-[414px]:gap-1">
    <BaseIconButton
      v-if="shouldShowMenuButton"
      :contained="false"
      :icon="BaseIconName.Bars"
      :size="BaseButtonSize.Small"
      :type="BaseButtonType.Secondary"
      class="shrink-0"
      @click="onClickMenuButton"
    />

    <BaseIconButton
      v-else
      :contained="false"
      :icon="sidebarToggleIcon"
      :size="BaseButtonSize.Small"
      :type="BaseButtonType.Secondary"
      class="shrink-0"
      data-testid="header-sidebar-toggle"
      @click="onClickSidebarToggle"
    />

    <BaseSelectBox
      v-if="hasMultipleCompanies && !isDesktop"
      v-model="selectedCompanyValue"
      :class="companySelectClass"
      :clearable="false"
      :options="selectOptions"
      :placeholder="t('SelectUnit')"
      :filter="true"
      :filter-placeholder="t('SearchCompanies')"
      :empty-filter-message="t('NoCompaniesFound')"
      :loading="isRemoteSearchLoading"
      :on-search="onSearchCompanies"
      :reset-filter-on-hide="true"
      :size="companySelectSize"
      :sorted="false"
      :variant="BaseSelectBoxVariant.BorderlessFilled"
      data-testid="header-company-select"
      @hide="onDropdownHide"
    />
  </div>
</template>
